import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ThumbsUp, ThumbsDown, Lightbulb, Sparkles, Plus, Trash2, Hammer, Share2, ChevronDown, ChevronUp, ShieldCheck, Compass } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { FADE_IN_UP_HIDDEN, FADE_IN_UP_SHOWN, STAGGER_CONTAINER, STAGGER_ITEM } from '../lib/animations';
import { isStaffOrAbove } from '../lib/tier';
import ConfirmDialog from '../components/ConfirmDialog';
import useUndoDelete from '../hooks/useUndoDelete';
import Seo from '../components/Seo';
import ReleaseBanner from '../components/ReleaseBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_budget-forecast-27/artifacts/w1b6b3hu_mini.png';
const MAX_SUGGESTION_TITLE_LENGTH = 140;
const MAX_SUGGESTION_DESCRIPTION_LENGTH = 2000;

const STATUS_PILL = {
  pending_review: 'bg-[#FFF7ED] text-[#9A3412]',
  proposed: 'bg-[#F1F5F9] text-[#475569]',
  planned: 'bg-[#DBEAFE] text-[#1E40AF]',
  in_progress: 'bg-[#FEF3C7] text-[#92400E]',
  released: 'bg-[#DCFCE7] text-[#166534]',
  rejected: 'bg-[#FEE2E2] text-[#991B1B]',
};

const STATUS_LABEL = {
  pending_review: 'Under Review',
  proposed: 'Under Consideration',
  planned: 'Planned',
  in_progress: 'In Progress',
  released: 'Released',
  rejected: 'Not Planned',
};

const ROADMAP_PILL = {
  now: 'bg-[#FEF3C7] text-[#92400E]',
  next: 'bg-[#DBEAFE] text-[#1E40AF]',
  later: 'bg-[#F1F5F9] text-[#475569]',
  considering: 'bg-[#FFF7ED] text-[#9A3412]',
  released: 'bg-[#DCFCE7] text-[#166534]',
};

const ROADMAP_LABEL = {
  now: 'Now',
  next: 'Next',
  later: 'Later',
  considering: 'Considering',
  released: 'Released',
};

const CATEGORY_OPTIONS = [
  { value: 'feature', label: 'Feature' },
  { value: 'bug', label: 'Bug' },
  { value: 'ux', label: 'UX / Design' },
  { value: 'integration', label: 'Integration' },
  { value: 'other', label: 'Other' },
];

const Home = () => {
  const { user } = useAuth();
  const isStaff = isStaffOrAbove(user);

  const [suggestions, setSuggestions] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [roadmapItems, setRoadmapItems] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [releasedOpen, setReleasedOpen] = useState(() => {
    try { return localStorage.getItem('fl_home_released_open') === '1'; } catch { return false; }
  });
  const [roadmapOpen, setRoadmapOpen] = useState(() => {
    // Default OPEN on first visit — the "what's coming" promise is the most
    // forward-looking content on this page and should be visible by default.
    // Persisted state wins thereafter.
    try {
      const v = localStorage.getItem('fl_home_roadmap_open');
      if (v === '1') return true;
      if (v === '0') return false;
      return true; // first visit
    } catch {
      return true;
    }
  });
  const [communityOpen, setCommunityOpen] = useState(() => {
    try { return localStorage.getItem('fl_home_community_open') === '1'; } catch { return false; }
  });
  const [mineOpen, setMineOpen] = useState(false);
  const [pendingDeleteSuggestion, setPendingDeleteSuggestion] = useState(null);
  const { scheduleDelete } = useUndoDelete();
  const [form, setForm] = useState({ title: '', description: '', category: 'feature' });
  const [submitting, setSubmitting] = useState(false);

  const toggleRoadmap = () => {
    setRoadmapOpen((v) => {
      const next = !v;
      try { localStorage.setItem('fl_home_roadmap_open', next ? '1' : '0'); } catch (e) { /* no-op */ }
      return next;
    });
  };
  const toggleCommunity = () => {
    setCommunityOpen((v) => {
      const next = !v;
      try { localStorage.setItem('fl_home_community_open', next ? '1' : '0'); } catch (e) { /* no-op */ }
      return next;
    });
  };
  const toggleReleased = () => {
    setReleasedOpen((v) => {
      const next = !v;
      try { localStorage.setItem('fl_home_released_open', next ? '1' : '0'); } catch (e) { /* no-op */ }
      return next;
    });
  };

  const fetchAll = useCallback(async () => {
    try {
      const baseRequests = [
        axios.get(`${API}/suggestions`, { withCredentials: true }),
        axios.get(`${API}/suggestions/mine`, { withCredentials: true }),
        axios.get(`${API}/roadmap`, { withCredentials: true }),
        axios.get(`${API}/site-settings`, { withCredentials: true }),
      ];
      if (isStaff) {
        baseRequests.push(axios.get(`${API}/suggestions/pending`, { withCredentials: true }));
      }
      const results = await Promise.all(baseRequests);
      setSuggestions(results[0].data);
      setMySubmissions(results[1].data);
      setRoadmapItems(results[2].data);
      setSiteSettings(results[3].data || {});
      if (isStaff && results[4]) setPendingCount(results[4].data.length);
    } catch (e) {
      toast.error('Failed to load home content');
    } finally {
      setLoading(false);
    }
  }, [isStaff]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleVote = async (suggestion_id, currentVote, newVote) => {
    const toSend = currentVote === newVote ? 'clear' : newVote;
    try {
      const response = await axios.post(`${API}/suggestions/${suggestion_id}/vote`, { vote: toSend }, { withCredentials: true });
      setSuggestions((prev) => prev.map((s) => (s.suggestion_id === suggestion_id ? response.data : s)));
    } catch (e) {
      toast.error('Failed to record vote');
    }
  };

  const handleStaffPatch = async (suggestion_id, patch) => {
    try {
      const res = await axios.patch(`${API}/suggestions/${suggestion_id}`, patch, { withCredentials: true });
      setSuggestions((prev) => prev.map((s) => (s.suggestion_id === suggestion_id ? res.data : s)));
      toast.success('Updated');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const handleSubmitSuggestion = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 3) {
      toast.error('Title must be at least 3 characters');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/suggestions`, form, { withCredentials: true });
      setMySubmissions((prev) => [response.data, ...prev]);
      toast.success('Thanks — your suggestion is queued for review. We\'ll email you with an update.', { duration: 6000 });
      setForm({ title: '', description: '', category: 'feature' });
      setDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit suggestion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (suggestion) => setPendingDeleteSuggestion(suggestion);

  const confirmDeleteSuggestion = () => {
    const s = pendingDeleteSuggestion;
    if (!s) return;
    setPendingDeleteSuggestion(null);
    setSuggestions((prev) => prev.filter((x) => x.suggestion_id !== s.suggestion_id));
    setMySubmissions((prev) => prev.filter((x) => x.suggestion_id !== s.suggestion_id));
    scheduleDelete({
      id: s.suggestion_id,
      label: 'Suggestion',
      executor: () => axios.delete(`${API}/suggestions/${s.suggestion_id}`, { withCredentials: true }),
      onUndo: () => fetchAll(),
    });
  };

  const handleShare = async () => {
    const shareUrl = window.location.origin;
    const shareData = {
      title: 'Forge Ledger',
      text: 'Forging a stronger financial future — track every penny, predict tomorrow\'s balance, catch shortfalls before they catch you.',
      url: shareUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); }
      catch (err) { if (err.name !== 'AbortError') toast.error('Share cancelled'); }
      return;
    }
    try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied to clipboard'); }
    catch (e) { toast.error('Unable to copy link'); }
  };

  const releasedItems = suggestions.filter((s) => s.status === 'released');
  const releasedRoadmap = roadmapItems.filter((r) => r.status === 'released');
  const releasedTotal = releasedItems.length + releasedRoadmap.length;
  const upcomingRoadmapCount = roadmapItems.filter((r) => r.status !== 'released').length;
  const activeCommunityItems = suggestions.filter((s) => !s.is_official && s.status !== 'released');
  const communityActiveCount = activeCommunityItems.length;
  const pendingOfMine = mySubmissions.filter((s) => s.status === 'pending_review');

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-12" data-testid="home-page">
      <Seo
        title="Home — Forge Ledger"
        description="Your Forge Ledger control room: roadmap, suggestions and what's coming next."
        path="/home"
        noindex
      />
      <ReleaseBanner />

      {isStaff && (
        <div className="mb-6 flex flex-wrap items-center gap-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg px-4 py-3" data-testid="staff-bar">
          <ShieldCheck className="w-4 h-4 text-[#1E40AF]" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1E40AF]">Staff tools</span>
          <Link to="/admin/moderation" className="text-sm font-medium text-[#0052FF] hover:underline inline-flex items-center gap-1.5" data-testid="staff-moderation-link">
            Moderation queue
            {pendingCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold leading-none" data-testid="pending-count-badge">
                {pendingCount}
              </span>
            )}
          </Link>
          <span className="text-[#CBD5E1]">·</span>
          <Link to="/admin/roadmap" className="text-sm font-medium text-[#0052FF] hover:underline">Edit roadmap</Link>
          <span className="text-[#CBD5E1]">·</span>
          <Link to="/admin/site-settings" className="text-sm font-medium text-[#0052FF] hover:underline">Edit home copy</Link>
          <span className="text-[#CBD5E1]">·</span>
          <Link to="/admin/edit/help" className="text-sm font-medium text-[#0052FF] hover:underline" data-testid="staff-edit-help">Edit Help &amp; FAQ</Link>
          <span className="text-[#CBD5E1]">·</span>
          <Link to="/admin/edit/about" className="text-sm font-medium text-[#0052FF] hover:underline" data-testid="staff-edit-about">Edit About us</Link>
          <span className="text-[#CBD5E1]">·</span>
          <Link to="/admin/edit/pricing" className="text-sm font-medium text-[#0052FF] hover:underline" data-testid="staff-edit-pricing">Edit Pricing bullets</Link>
          <span className="text-[#CBD5E1]">·</span>
          <Link to="/admin/tiers" className="text-sm font-medium text-[#0052FF] hover:underline" data-testid="staff-manage-tiers">Manage user tiers</Link>
          <span className="text-[#CBD5E1]">·</span>
          <Link to="/admin/qa-checklist" className="text-sm font-medium text-[#0052FF] hover:underline" data-testid="staff-qa-checklist">QA checklist</Link>
          <span className="text-[#CBD5E1]">·</span>
          <Link to="/admin/pre-deploy-checklist" className="text-sm font-medium text-[#0052FF] hover:underline" data-testid="staff-pre-deploy-checklist">Pre-deploy checklist</Link>
        </div>
      )}

      <motion.section initial={FADE_IN_UP_HIDDEN} animate={FADE_IN_UP_SHOWN} className="text-center mb-16" data-testid="hero-section">
        <div className="inline-flex items-center gap-4 mb-6">
          <img src={LOGO_URL} alt="Forge Ledger" className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-xl object-cover" />
          <span className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#020617]">Forge Ledger</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-[#020617] mb-4 leading-tight whitespace-pre-line" data-testid="hero-tagline">
          {siteSettings.hero_tagline || 'Forging a stronger\nfinancial future.'}
        </h1>
        <p className="text-base sm:text-lg text-[#64748B] max-w-2xl mx-auto mb-8 space-y-1">
          <span className="block">Track every penny.</span>
          <span className="block">Predict tomorrow&apos;s balance.</span>
          <span className="block">Catch shortfalls before they catch you.</span>
          <span className="block">A personal finance toolkit built around you.</span>
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <a href="/dashboard" className="inline-flex items-center gap-2 bg-[#0052FF] text-white rounded-lg px-6 py-3 font-medium hover:bg-[#003ECC] transition-colors" data-testid="cta-dashboard">
            <Hammer className="w-4 h-4" />
            Open Dashboard
          </a>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-white border border-[#E2E8F0] text-[#020617] hover:bg-[#F8FAFC]" data-testid="hero-suggest-button">
                <Lightbulb className="w-4 h-4 mr-2" />
                Suggest an improvement
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white" data-testid="suggest-dialog">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-[#020617]">Share your idea</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-[#64748B] -mt-2">
                Suggestions are reviewed by our team before being displayed for community voting.
              </p>
              <form onSubmit={handleSubmitSuggestion} className="space-y-4" data-testid="suggestion-form">
                <div>
                  <Label htmlFor="sug-title">Title</Label>
                  <Input id="sug-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Export to PDF for accountants" maxLength={MAX_SUGGESTION_TITLE_LENGTH} required className="mt-1.5" data-testid="suggestion-title-input" />
                </div>
                <div>
                  <Label htmlFor="sug-cat">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="suggestion-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="sug-desc">Description (optional)</Label>
                  <Textarea id="sug-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell us more about what you'd like..." maxLength={MAX_SUGGESTION_DESCRIPTION_LENGTH} rows={4} className="mt-1.5" data-testid="suggestion-desc-input" />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button type="submit" disabled={submitting} className="flex-1 bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="suggestion-submit-button">{submitting ? 'Submitting...' : 'Submit for review'}</Button>
                  <Button type="button" onClick={() => setDialogOpen(false)} className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]">Cancel</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          <Button onClick={handleShare} className="bg-white border border-[#E2E8F0] text-[#020617] hover:bg-[#F8FAFC]" data-testid="hero-share-button">
            <Share2 className="w-4 h-4 mr-2" />
            Share Forge Ledger
          </Button>
        </div>
      </motion.section>

      {/* ROADMAP — staff-curated, no voting */}
      <motion.section variants={STAGGER_CONTAINER} initial="hidden" animate="show" className="mb-16" data-testid="roadmap-section">
        <button
          type="button"
          onClick={toggleRoadmap}
          aria-expanded={roadmapOpen}
          data-testid="roadmap-toggle"
          className="w-full flex items-center justify-between bg-white border border-[#E2E8F0] rounded-lg px-6 py-4 hover:border-[#CBD5E1] transition-colors"
        >
          <div className="flex items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
              <Compass className="w-5 h-5 text-[#1E40AF]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0052FF]">Roadmap</div>
              <div className="text-lg font-bold text-[#020617]">Upcoming Improvements</div>
              <div className="text-sm text-[#64748B]" data-testid="roadmap-count">
                {upcomingRoadmapCount} item{upcomingRoadmapCount === 1 ? '' : 's'} under the hammer
              </div>
            </div>
          </div>
          {roadmapOpen ? (
            <span className="w-8 h-8 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-[#475569] transition-colors flex-shrink-0">
              <ChevronUp className="w-4 h-4" />
            </span>
          ) : (
            <span className="w-8 h-8 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-[#475569] transition-colors flex-shrink-0">
              <ChevronDown className="w-4 h-4" />
            </span>
          )}
        </button>
        {roadmapOpen && (
          <div className="mt-4" data-testid="roadmap-content">
            <p className="text-sm text-[#64748B] mb-4 max-w-2xl">
              {siteSettings.roadmap_intro || 'What we\'re working on next.'}
            </p>
            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052FF]" /></div>
            ) : upcomingRoadmapCount === 0 ? (
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-10 text-center">
                <Compass className="w-8 h-8 text-[#64748B] mx-auto mb-3" />
                <p className="text-[#64748B]">The Forge Ledger team hasn&apos;t published a roadmap yet. Check back soon.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {roadmapItems.filter((r) => r.status !== 'released').map((r) => (
                  <motion.div variants={STAGGER_ITEM} key={r.roadmap_id} className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all" data-testid={`roadmap-card-${r.roadmap_id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-[#020617] leading-tight">{r.title}</h3>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${ROADMAP_PILL[r.status]}`}>{ROADMAP_LABEL[r.status]}</span>
                    </div>
                    {r.description && <p className="text-sm text-[#64748B] leading-relaxed whitespace-pre-line">{r.description}</p>}
                    {(r.category || r.eta || isStaff) && (
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[#64748B]">
                        {r.category && <span className="px-2 py-0.5 rounded bg-[#F1F5F9]">{r.category}</span>}
                        {r.eta && <span>· ETA {r.eta}</span>}
                        {isStaff && (
                          r.visibility === 'internal' ? (
                            <span
                              className="ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]"
                              title="Hidden from customers — staff only"
                              data-testid={`home-roadmap-internal-pill-${r.roadmap_id}`}
                            >
                              Internal
                            </span>
                          ) : (
                            <span
                              className="ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534]"
                              title="Visible to customers on the public roadmap"
                              data-testid={`home-roadmap-customer-pill-${r.roadmap_id}`}
                            >
                              Customer
                            </span>
                          )
                        )}
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.section>

      {/* COMMUNITY SUGGESTIONS — collapsible */}
      <motion.section variants={STAGGER_CONTAINER} initial="hidden" animate="show" className="mb-16" data-testid="community-section">
        <button
          type="button"
          onClick={toggleCommunity}
          aria-expanded={communityOpen}
          data-testid="community-toggle"
          className="w-full flex items-center justify-between bg-white border border-[#E2E8F0] rounded-lg px-6 py-4 hover:border-[#CBD5E1] transition-colors"
        >
          <div className="flex items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-[#B45309]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0052FF]">Community</div>
              <div className="text-lg font-bold text-[#020617]">Suggestions from users</div>
              <div className="text-sm text-[#64748B]" data-testid="community-count">
                {communityActiveCount === 0
                  ? 'Be the first to share an idea'
                  : `${communityActiveCount} possibilit${communityActiveCount === 1 ? 'y' : 'ies'} to forge ${pendingOfMine.length > 0 ? `· ${pendingOfMine.length} of yours under review` : ''}`}
              </div>
            </div>
          </div>
          {communityOpen ? (
            <span className="w-8 h-8 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-[#475569] transition-colors flex-shrink-0">
              <ChevronUp className="w-4 h-4" />
            </span>
          ) : (
            <span className="w-8 h-8 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-[#475569] transition-colors flex-shrink-0">
              <ChevronDown className="w-4 h-4" />
            </span>
          )}
        </button>

        {communityOpen && (
          <div className="mt-4" data-testid="community-content">
            <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
              <p className="text-sm text-[#64748B] max-w-2xl">
                {siteSettings.suggestions_intro || 'Have an idea that would make Forge Ledger better? Suggestions are reviewed by our team before being displayed here. Those with potential will be shown for community voting in due course.'}
              </p>
              <Button onClick={() => setDialogOpen(true)} className="bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="add-suggestion-button">
                <Plus className="w-4 h-4 mr-2" />
                Add yours
              </Button>
            </div>

            {/* My pending submissions card */}
            {pendingOfMine.length > 0 && (
              <div className="mb-4 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg px-4 py-3" data-testid="my-pending-banner">
                <button type="button" onClick={() => setMineOpen((v) => !v)} className="w-full flex items-center justify-between" aria-expanded={mineOpen}>
                  <span className="text-sm text-[#9A3412] font-medium">
                    You have {pendingOfMine.length} suggestion{pendingOfMine.length === 1 ? '' : 's'} under review.
                  </span>
                  {mineOpen ? <ChevronUp className="w-4 h-4 text-[#9A3412]" /> : <ChevronDown className="w-4 h-4 text-[#9A3412]" />}
                </button>
                {mineOpen && (
                  <ul className="mt-3 space-y-2">
                    {pendingOfMine.map((s) => (
                      <li key={s.suggestion_id} className="flex items-center justify-between gap-3 text-sm bg-white rounded-md px-3 py-2 border border-[#FED7AA]">
                        <span className="font-medium text-[#020617]">{s.title}</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_PILL.pending_review}`}>Under Review</span>
                          <button onClick={() => handleDelete(s)} className="text-[#64748B] hover:text-[#EF4444]" aria-label="Withdraw suggestion" data-testid={`withdraw-${s.suggestion_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {activeCommunityItems.length === 0 && !loading ? (
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-12 text-center">
                <Sparkles className="w-8 h-8 text-[#64748B] mx-auto mb-3" />
                <p className="text-[#64748B] mb-4">No community suggestions yet. Be the first to share an idea!</p>
                <Button onClick={() => setDialogOpen(true)} className="bg-[#0052FF] text-white hover:bg-[#003ECC]">
                  <Lightbulb className="w-4 h-4 mr-2" />
                  Suggest something
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {activeCommunityItems.map((item) => (
                  <SuggestionCard key={item.suggestion_id} item={item} onVote={handleVote} onDelete={handleDelete} onStaffPatch={isStaff ? handleStaffPatch : null} currentUserId={user?.user_id} userRole={user?.role} />
                ))}
              </div>
            )}
          </div>
        )}
      </motion.section>

      {/* RELEASED IMPROVEMENTS (renamed from Archive) */}
      <motion.section initial={FADE_IN_UP_HIDDEN} animate={FADE_IN_UP_SHOWN} className="mb-16" data-testid="released-section">
        <button onClick={toggleReleased} className="w-full flex items-center justify-between bg-white border border-[#E2E8F0] rounded-lg px-6 py-4 hover:border-[#CBD5E1] transition-colors" aria-expanded={releasedOpen} data-testid="released-toggle">
          <div className="flex items-center gap-3 text-left">
            <div className="w-10 h-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-[#166534]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0052FF]">Released</div>
              <div className="text-lg font-bold text-[#020617]">Released Improvements</div>
              <div className="text-sm text-[#64748B]" data-testid="released-count">
                {releasedTotal} item{releasedTotal === 1 ? '' : 's'} forged
              </div>
            </div>
          </div>
          {releasedOpen ? (
            <span className="w-8 h-8 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-[#475569] transition-colors flex-shrink-0">
              <ChevronUp className="w-4 h-4" />
            </span>
          ) : (
            <span className="w-8 h-8 rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-[#475569] transition-colors flex-shrink-0">
              <ChevronDown className="w-4 h-4" />
            </span>
          )}
        </button>
        {releasedOpen && (
          <div className="mt-4" data-testid="released-content">
            <p className="text-sm text-[#64748B] mb-4 max-w-2xl">
              {siteSettings.released_intro || 'Improvements the community helped us shape. Thanks for the feedback.'}
            </p>
            {releasedTotal === 0 ? (
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-8 text-center">
                <p className="text-sm text-[#64748B]">No released items yet — watch this space.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {releasedRoadmap.length > 0 && (
                  <div data-testid="released-roadmap-block">
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-3">From the team</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {releasedRoadmap.map((r) => (
                        <div key={r.roadmap_id} className="bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid={`released-roadmap-card-${r.roadmap_id}`}>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-[#020617] leading-tight">{r.title}</h3>
                            <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${ROADMAP_PILL.released}`}>
                              {ROADMAP_LABEL.released}
                            </span>
                          </div>
                          {r.description && <p className="text-sm text-[#64748B] leading-relaxed whitespace-pre-line">{r.description}</p>}
                          {(r.category || isStaff) && (
                            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-[#64748B]">
                              {r.category && <span className="px-2 py-0.5 rounded bg-[#F1F5F9]">{r.category}</span>}
                              {isStaff && (
                                r.visibility === 'internal' ? (
                                  <span
                                    className="ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#FEF3C7] text-[#92400E]"
                                    title="Hidden from customers — staff only"
                                    data-testid={`released-roadmap-internal-pill-${r.roadmap_id}`}
                                  >
                                    Internal
                                  </span>
                                ) : (
                                  <span
                                    className="ml-auto text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#166534]"
                                    title="Visible to customers on the public roadmap"
                                    data-testid={`released-roadmap-customer-pill-${r.roadmap_id}`}
                                  >
                                    Customer
                                  </span>
                                )
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {releasedItems.length > 0 && (
                  <div data-testid="released-community-block">
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-3">From the community</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {releasedItems.map((item) => (
                        <SuggestionCard key={item.suggestion_id} item={item} onVote={null} onDelete={null} onStaffPatch={isStaff ? handleStaffPatch : null} currentUserId={user?.user_id} userRole={user?.role} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </motion.section>

      <ConfirmDialog
        open={!!pendingDeleteSuggestion}
        onOpenChange={(open) => { if (!open) setPendingDeleteSuggestion(null); }}
        title="Withdraw this suggestion?"
        description={pendingDeleteSuggestion ? `"${pendingDeleteSuggestion.title}" will be removed.` : 'This cannot be undone.'}
        confirmLabel="Withdraw"
        onConfirm={confirmDeleteSuggestion}
        testId="delete-suggestion-dialog"
      />
    </div>
  );
};

const STAFF_STATUS_OPTIONS = [
  { value: 'proposed', label: 'Under Consideration' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'released', label: 'Released' },
  { value: 'rejected', label: 'Not Planned' },
];

const SuggestionCard = ({ item, onVote, onDelete, onStaffPatch, currentUserId, userRole }) => {
  const isStaff = userRole === 'staff' || userRole === 'admin' || userRole === 'owner';
  const canDelete = onDelete && (item.user_id === currentUserId || isStaff);
  return (
    <motion.div variants={STAGGER_ITEM} className="bg-white border border-[#E2E8F0] rounded-lg p-5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-[#CBD5E1] transition-all duration-300" data-testid={`suggestion-card-${item.suggestion_id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-lg font-semibold text-[#020617] leading-tight">{item.title}</h3>
        <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${STATUS_PILL[item.status] || STATUS_PILL.proposed}`}>
          {STATUS_LABEL[item.status] || item.status}
        </span>
      </div>
      {item.description && (<p className="text-sm text-[#64748B] mb-4 leading-relaxed">{item.description}</p>)}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-[#64748B]">
          — {item.user_name}
          {item.category && item.category !== 'other' && (<span className="ml-2 px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[10px] uppercase tracking-wider">{item.category}</span>)}
        </div>
        <div className="flex items-center gap-2">
          {onVote && (
            <>
              <button onClick={() => onVote(item.suggestion_id, item.current_user_vote, 'up')} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${item.current_user_vote === 'up' ? 'bg-[#10B981] text-white' : 'bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]'}`} data-testid={`upvote-${item.suggestion_id}`}>
                <ThumbsUp className="w-3.5 h-3.5" />
                {item.votes_up}
              </button>
              <button onClick={() => onVote(item.suggestion_id, item.current_user_vote, 'down')} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${item.current_user_vote === 'down' ? 'bg-[#EF4444] text-white' : 'bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]'}`} data-testid={`downvote-${item.suggestion_id}`}>
                <ThumbsDown className="w-3.5 h-3.5" />
                {item.votes_down}
              </button>
            </>
          )}
          {canDelete && (
            <button onClick={() => onDelete(item)} className="text-[#64748B] hover:text-[#EF4444] transition-colors ml-1" data-testid={`delete-suggestion-${item.suggestion_id}`} aria-label="Delete suggestion">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      {isStaff && onStaffPatch && (
        <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex flex-wrap items-center gap-2" data-testid={`staff-controls-${item.suggestion_id}`}>
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#1E40AF] inline-flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" />
            Staff
          </span>
          <Select value={item.status} onValueChange={(v) => onStaffPatch(item.suggestion_id, { status: v, notify_author: true })}>
            <SelectTrigger className="h-8 w-44 text-xs" data-testid={`staff-status-${item.suggestion_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAFF_STATUS_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={item.category || 'other'} onValueChange={(v) => onStaffPatch(item.suggestion_id, { category: v, notify_author: false })}>
            <SelectTrigger className="h-8 w-36 text-xs" data-testid={`staff-category-${item.suggestion_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((c) => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
            </SelectContent>
          </Select>
          <span className="text-[10px] text-[#64748B]">Status change emails author</span>
        </div>
      )}
    </motion.div>
  );
};

export default Home;
